import React, { useState, useMemo, useCallback } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress,
  Divider,
  MenuItem,
} from "@mui/material";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { updateDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getSubjectsForTutorType } from "../constants";
import { useTranslation } from "react-i18next";

const AlertSnackbar = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const animatedComponents = makeAnimated();

const RTL_TEXTFIELD_PROPS = {
  InputLabelProps: {
    sx: {
      direction: "rtl",
      textAlign: "right",
      maxWidth: "calc(100% - 24px)",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  InputProps: {
    sx: {
      "& .MuiOutlinedInput-input": {
        direction: "rtl",
        textAlign: "right",
      },
    },
  },
};

const EditTeacherForm = ({ teacher, onClose, setTeachers }) => {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tutorType, setTutorType] = useState(teacher.tutorType || "Teacher");
  const [subjectIds, setSubjectIds] = useState(() => {
    const allowedSubjectIds = new Set(
      getSubjectsForTutorType(teacher.tutorType || "Teacher").map((subject) => subject.id)
    );
    return (Array.isArray(teacher.subjectIds) ? teacher.subjectIds : []).filter((id) =>
      allowedSubjectIds.has(id)
    );
  });
  const [inputValue, setInputValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const nonEmpty = (v) => (Array.isArray(v) ? v.length > 0 : String(v ?? "").trim().length > 0);

  const validateForm = () => {
    const errs = {};

    if (!nonEmpty(teacher.firstName)) errs.firstName = t("Required");
    if (!nonEmpty(teacher.lastName)) errs.lastName = t("Required");
    if (!nonEmpty(teacher.email)) errs.email = t("Required");
    if (!nonEmpty(teacher.phone)) errs.phone = t("Required");
    if (!nonEmpty(tutorType)) errs.tutorType = t("Select tutor type");

    if (subjectIds.length === 0) errs.modules = t("Select at least one module");

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const subjectOptions = useMemo(() => {
    return getSubjectsForTutorType(tutorType).map((subject) => ({
      value: subject.id,
      label: `${subject.name} (${subject.level})`,
    }));
  }, [tutorType]);

  const selectedSubjects = useMemo(
    () => subjectOptions.filter((option) => subjectIds.includes(option.value)),
    [subjectIds, subjectOptions]
  );

  const handleSubjectChange = useCallback((selectedOptions) => {
    const newSubjects = selectedOptions ? selectedOptions.map((option) => option.value) : [];
    setSubjectIds(newSubjects);
    setFieldErrors((prev) => ({ ...prev, modules: undefined }));
  }, []);

  const handleTutorTypeChange = (event) => {
    const nextTutorType = event.target.value;
    const allowedSubjectIds = new Set(
      getSubjectsForTutorType(nextTutorType).map((subject) => subject.id)
    );

    setTutorType(nextTutorType);
    setSubjectIds((currentSubjectIds) =>
      currentSubjectIds.filter((subjectId) => allowedSubjectIds.has(subjectId))
    );
  };

  const handleInputChange = useCallback((newValue, { action }) => {
    if (action === "input-change") setInputValue(newValue);
    return newValue;
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const teacherRef = doc(db, "teachers", teacher.id);
      await updateDoc(teacherRef, {
        subjectIds,
        tutorType: tutorType || "Teacher",
      });

      setTeachers((prev) =>
        prev.map((currentTeacher) =>
          currentTeacher.id === teacher.id
            ? {
                ...currentTeacher,
                subjectIds,
                tutorType: tutorType || "Teacher",
              }
            : currentTeacher
        )
      );

      setSnackbarMessage(t("Changes saved successfully"));
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      onClose();
    } catch (err) {
      console.error("Error updating teacher:", err);
      setSnackbarMessage(t("An error occurred while saving changes"));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      dir="rtl"
      sx={{ display: "flex", flexDirection: "column", gap: 2, direction: "rtl" }}
    >
      <Typography sx={{ fontWeight: 800 }}>{t("Basic information")}</Typography>
      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, direction: "rtl" }}>
        <TextField
          label={t("First name")}
          fullWidth
          value={teacher.firstName}
          disabled
          error={Boolean(fieldErrors.firstName)}
          helperText={fieldErrors.firstName || ""}
        />
        <TextField
          label={t("Last name")}
          fullWidth
          value={teacher.lastName}
          disabled
          error={Boolean(fieldErrors.lastName)}
          helperText={fieldErrors.lastName || ""}
        />
        <TextField
          label={t("Email")}
          fullWidth
          value={teacher.email}
          disabled
          error={Boolean(fieldErrors.email)}
          helperText={fieldErrors.email || ""}
        />
        <TextField
          label={t("Phone")}
          fullWidth
          value={teacher.phone}
          disabled
          error={Boolean(fieldErrors.phone)}
          helperText={fieldErrors.phone || ""}
        />

        <TextField
          select
          label={t("Tutor type")}
          fullWidth
          value={tutorType}
          onChange={handleTutorTypeChange}
          error={Boolean(fieldErrors.tutorType)}
          helperText={fieldErrors.tutorType || ""}
          {...RTL_TEXTFIELD_PROPS}
        >
          <MenuItem value="Teacher">{t("Teacher")}</MenuItem>
          <MenuItem value="Coach">{t("Coach")}</MenuItem>
        </TextField>

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t("Modules")}
          </Typography>
          <Select
            isMulti
            closeMenuOnSelect={false}
            components={animatedComponents}
            options={subjectOptions}
            value={selectedSubjects}
            onChange={handleSubjectChange}
            placeholder={t("Select one or more modules")}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            styles={{
              control: (base) => ({
                ...base,
                borderColor: fieldErrors.modules ? "#d32f2f" : base.borderColor,
                boxShadow: fieldErrors.modules ? "0 0 0 1px #d32f2f" : base.boxShadow,
                "&:hover": {
                  borderColor: fieldErrors.modules ? "#d32f2f" : base.borderColor,
                },
              }),
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isDisabled
                  ? "#f5f5f5"
                  : state.isSelected
                    ? "#e0e0e0"
                    : provided.backgroundColor,
                color: state.isDisabled ? "#ccc" : provided.color,
                cursor: state.isDisabled ? "not-allowed" : "default",
              }),
            }}
          />
          {fieldErrors.modules && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
              {fieldErrors.modules}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={isSubmitting}
        startIcon={isSubmitting && <CircularProgress size={20} />}
      >
        {isSubmitting ? t("Saving...") : t("Save changes")}
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <AlertSnackbar onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </AlertSnackbar>
      </Snackbar>
    </Box>
  );
};

export default EditTeacherForm;
